import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { Autocomplete, TextField } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import { ref, update, get } from 'firebase/database';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';

import { createNotebooksFunc, getStudentView, getStudents } from '../../../Common/Database';
import { database } from '../../../Common/Firebase';
import { throwErrorRollbar } from '../../../index';
import { setStudentNames } from '../../../redux/slices/nameStudentsSlice';

import '../../../Components/ModalMaterial/style.scss';
const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const EditGroupModal = ({ open, setOpen, studentGroup, fetchStudentsGroupView, onAttachStudent }) => {
  const { t } = useTranslation();
  const params = useParams();
  const dispatch = useDispatch();

  const [studetnItems, setStudetnItems] = useState([]);
  const [groupName, setGroupName] = useState(studentGroup ? studentGroup.groupName : '');
  const [groupNameDirty, setGroupNameDirty] = useState(false);
  const [groupNameError, setGroupNameError] = useState('');
  const [isFormValid, setIsFormValid] = useState(!!studentGroup);
  const [selectedStudents, setSelectedStudents] = useState(studentGroup ? studentGroup.students : []);
  const [isFocused, setIsFocused] = useState(false);
  const [student, setStudent] = useState([]);
  const [subjectList, setSubjectList] = useState();
  const [activeGroupSub, setActiveGroupSub] = useState([]);

  const groupNameHandler = e => {
    const value = e.target.value;
    setGroupNameDirty(true);

    if (value === undefined || value.trim() === '') {
      setGroupNameError(t(''));
    } else if (!/^[A-Za-zА-Яа-яҐґЄєІіЇї0-9\s/'/-]*$/.test(value.trim())) {
      setGroupNameError(t('The name must consist of letters and numbers'));
    } else {
      setGroupNameError('');
    }
  };

  const blurHandler = e => {
    const value = e.target.value;
    setGroupNameDirty(true);
    if (value === undefined || value.trim() === '') {
      setGroupNameError(t('field is required'));
    } else {
      setGroupNameError('');
    }
  };

  const fetchStudentsListData = async () => {
    const studentsList = await getStudents();
    if (studentsList) {
      let mapList = Object.values(studentsList);
      setStudetnItems(mapList);
      dispatch(setStudentNames(mapList));
    }
  };

  useEffect(() => {
    const fetchDataAndProcess = async () => {
      await fetchStudentsListData();

      if (groupNameError || !groupName.trim() || selectedStudents.length === 0) {
        setIsFormValid(false);
      } else {
        setIsFormValid(true);
      }

      if (studentGroup && studentGroup.subjects) {
        const activeSubs = Object.values(studentGroup.subjects).filter(item => {
          return item.activeSub && item.attachedTeacher;
        });
        setActiveGroupSub(activeSubs);
      }

      if (student.length > 0 && activeGroupSub.length > 0) {
        const subjectsArray = student.map(singleStudent => singleStudent.subjects);
        const updatedSubjectsArray = updateSubjectsWithTeacherInfo(subjectsArray, activeGroupSub);
        console.log('updatedSubjectsArray:', updatedSubjectsArray);
      }
    };

    fetchDataAndProcess();
  }, [groupNameError, groupName, selectedStudents, student, subjectList, studentGroup]);

  const updateSubjectsWithTeacherInfo = (subjectsArray, activeGroupSub) => {
    return subjectsArray.map(subjects => {
      const updatedSubjects = {};

      for (const subjectName in subjects) {
        const subjectInfo = subjects[subjectName];
        const matchedSubject = activeGroupSub.find(item => item.name === subjectName);
        if (matchedSubject) {
          updatedSubjects[subjectName] = {
            ...subjectInfo,
            attachedTeacher: matchedSubject.attachedTeacher,
          };
        } else {
          updatedSubjects[subjectName] = subjectInfo;
        }
      }

      return updatedSubjects;
    });
  };

  const onSubmitFunc = async () => {
    const selectedStudentData = selectedStudents.map(student => ({
      FIO: student.FIO,
      email: student.email.replace(/\./g, ''),
    }));
    const emailsArr = selectedStudentData.map(item => item.email);
    const activeGroupSub = Object.values(studentGroup.subjects).filter(item => {
      return item.activeSub && item.attachedTeacher;
    });
    const allStudents = [];

    emailsArr.map(async email => {
      await getStudentView(email).then(response => {
        const resp = response.val();
        let stSubjects = Object.values(resp.subjects);

        allStudents.push(resp);
        setSubjectList(stSubjects);
      });

      const activeTeachers = activeGroupSub
        .filter(item => item.activeSub && item.attachedTeacher)
        .reduce((acc, cur) => {
          acc[cur.name] = cur.attachedTeacher;
          return acc;
        }, {});

      const studentsWithTeachers = allStudents.map(student => {
        const updatedSubjects = { ...student.subjects };

        for (const subjectName in student.subjects) {
          const subjectInfo = student.subjects[subjectName];
          const isSubjectInGroup = activeGroupSub.some(
            item => item.name === subjectName && item.activeSub && item.attachedTeacher,
          );

          if (isSubjectInGroup) {
            const attachedTeacher = activeTeachers[subjectName];
            const email = student.email.replace(/\./g, '');

            if (!subjectInfo.getNotebook) {
              createNotebooksFunc(email, subjectName);
            }

            updatedSubjects[subjectName] = {
              ...subjectInfo,
              attachedTeacher: attachedTeacher || null,
              activeSub: true,
              getNotebook: true,
            };
          }
        }

        return {
          ...student,
          subjects: updatedSubjects,
        };
      });

      studentsWithTeachers.forEach(async student => {
        const email = student.email.replace(/\./g, '');
        try {
          await update(ref(database, `Students/${email}`), student);
          console.log('Updated student:', email);
        } catch (error) {
          console.log('Error updating student:', email, error);
          throwErrorRollbar(error);
        }
      });
    });

    setStudent(allStudents);

    try {
      const snapshot = await get(ref(database, `StudentsGroup/${params.id}`));
      const currentData = snapshot.val();

      if (currentData) {
        currentData.groupName = groupName;
        currentData.students = selectedStudentData;

        await update(ref(database, `StudentsGroup/${params.id}`), currentData);

        setOpen(false);
        fetchStudentsGroupView();
      }
    } catch (error) {
      console.log(error);
      throwErrorRollbar(error);
    }
  };

  return (
    <div
      className={open ? 'modal-form modal-form--open' : 'modal-form'}
      onClick={e => {
        if (!isFocused) {
          setOpen(false);
        }
        e.stopPropagation();
      }}
    >
      <div
        className="modal-form-content"
        onClick={e => {
          e.stopPropagation();
        }}
      >
        <form>
          <div className="modal-header">
            <h2>{t('Update a group of students')}:</h2>
            <button
              className="btn btn-icon"
              onClick={e => {
                setOpen(false);
                e.preventDefault();
              }}
            >
              <span className="icon icon-plus"></span>
            </button>
          </div>

          <div className="modal-body" id="modal-body">
            <div className="form-control autocomplete-control">
              <Autocomplete
                id="groupName"
                options={[]}
                value={groupName}
                onInputChange={(e, v) => setGroupName(v)}
                onBlur={blurHandler}
                freeSolo
                renderInput={params => (
                  <TextField
                    {...params}
                    label={t('groupName')}
                    value={groupName}
                    onChange={groupNameHandler}
                    name="groupName"
                    placeholder=" "
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                  />
                )}
              />
              {groupNameDirty && groupNameError && <span className="error">{groupNameError}</span>}
            </div>

            <div className="form-control autocomplete-control">
              <Autocomplete
                multiple
                id="students-arr"
                options={studetnItems}
                disableCloseOnSelect
                getOptionLabel={option => option.FIO}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox icon={icon} checkedIcon={checkedIcon} checked={selected} />
                    {option.FIO}
                  </li>
                )}
                value={selectedStudents}
                onChange={(event, newValue) => setSelectedStudents(newValue)}
                renderInput={params => (
                  <TextField
                    {...params}
                    label={t('select students')}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                  />
                )}
              />
            </div>
          </div>

          <div className="modal-footer">
            <button
              className="btn btn-primary more btn-active"
              onClick={e => {
                onSubmitFunc();
                e.preventDefault();
              }}
              disabled={!isFormValid}
            >
              {t('update')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditGroupModal;
